import { RemarkProvider, useSessionInfo, useShouldAppear, useVendor } from '@outdoorly/react-chat-sdk';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import theme from './theme';
import { ChakraProvider, Flex, useMediaQuery } from '@chakra-ui/react';
import { ChatConfigWindow } from 'src';
import { Chat } from './Chat';
import { Store, widgetVisibleKey } from './store';
import { Activator, openWidget } from './components/Activator';
import { LabelUpdater } from './utils/labels';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import weakMemoize from '@emotion/weak-memoize';
import Frame from 'react-frame-component';
import { DESKTOP_MEDIA_QUERY } from './constants';
import useWindowDimensions, { useMeasuredSize, useMemoizedFrame } from './utils/hooks';
import { BrowserDimensionsContext } from './utils/dimensions';
import { AnimatePresence } from 'framer-motion';
import posthog from 'posthog-js';
import { addRemarkListener, removeRemarkListener } from '@outdoorly/chat-sdk';
import { closeWidget } from './components/ChatHeader';
import { getCastedFeatureFlag, FeatureFlags } from './store/feature-flags';

export const INITIAL_MESSAGE_PAGE_SIZE = 50;
const showWidget = () => {
  Store.update((s) => {
    s.widgetVisible = true;
  });
  localStorage.setItem(widgetVisibleKey, 'visible');
};
const hideWidget = () => {
  Store.update((s) => {
    s.widgetVisible = false;
  });
  localStorage.setItem(widgetVisibleKey, 'hidden');
};

addRemarkListener('open', openWidget);
addRemarkListener('close', closeWidget);
addRemarkListener('show', showWidget);
addRemarkListener('hide', hideWidget);
declare let window: ChatConfigWindow;
const ChatAndActivator = React.memo(() => {
  const { initialized, shouldAppear } = useShouldAppear();
  const { sessionInfo } = useSessionInfo();
  const { vendor } = useVendor();

  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false);
  const isWidgetVisible = Store.useState((s) => s.widgetVisible);
  const initPostHog = useCallback(() => {
    if (!sessionInfo?.lead || sessionInfo.isControlGroup === undefined) return; // Wait for a lead before init and ensure definitive answer on control group

    if (!!process.env.POSTHOG_APP_ID) {
      posthog.init(process.env.POSTHOG_APP_ID, {
        api_host: 'https://p.trybirdie.com',
        autocapture: false,
        loaded: function (posthog) {
          // Only identify if our current id is not the session lead
          if (vendor) posthog.group('vendor', vendor.id); // this makes sure we send a groupId with any /decide/ calls
          if (posthog.get_distinct_id() !== sessionInfo.lead && sessionInfo.lead) {
            posthog.identify(sessionInfo.lead);

            if (sessionInfo.vendorLiftTestId) {
              posthog.capture(
                'register-for-lift-test',
                {
                  $set_once: {
                    vendorLiftTestId: sessionInfo.vendorLiftTestId,
                    isControlGroup: sessionInfo.isControlGroup
                  }
                },
                { send_instantly: true }
              );
            }

            // Wait to get feature flags from Posthog until after we have been identified
            setTimeout(() => posthog.reloadFeatureFlags(), 150);
          } else {
            setTimeout(() => posthog.reloadFeatureFlags(), 5); // Can't reload inside the loaded function apparently -- so we do it just a tiny bit later
          }
        },
        advanced_disable_decide: true,
        capture_pageview: false,
        request_batching: false
      });

      //get feat flags from local storage
      const lastFeatFlagTime = localStorage.getItem('lastSetRemarkFeatureFlags');
      const lastFeatureFlags = localStorage.getItem('remarkFeatureFlags');

      // If we have locals cached,
      // Set them and use them until newer vals come in.
      if (
        lastFeatFlagTime &&
        (new Date().getTime() - new Date(Number(lastFeatFlagTime)).getTime()) / 1000 / 60 / 60 < 1 &&
        lastFeatureFlags
      ) {
        const featureFlags = JSON.parse(lastFeatureFlags) as FeatureFlags;
        Object.keys(featureFlags).forEach((flag) => {
          Store.update((s) => {
            s.featureFlags[flag] = featureFlags[flag];
          });
        });
        setFeatureFlagsLoaded(true);
      }

      // Once newer values come in, then update the locals
      const remarkFlags: FeatureFlags = {};
      try {
        posthog.onFeatureFlags(function () {
          localStorage.setItem('lastSetRemarkFeatureFlags', new Date().getTime().toString());
          const currentStateFeatureFlags = Store.getRawState().featureFlags;
          posthog.featureFlags.getFlags().forEach((flag: keyof FeatureFlags) => {
            // this method will return the value for the flag and ONLY create
            // a feature_flag_called event in posthog when the value changes.
            const flagValue = getCastedFeatureFlag(flag, currentStateFeatureFlags[flag]);
            remarkFlags[flag] = flagValue;
            Store.update((s) => {
              s.featureFlags[flag] = flagValue;
            });
          });
          localStorage.setItem('remarkFeatureFlags', JSON.stringify(remarkFlags));
          setFeatureFlagsLoaded(true);
        });
      } catch {
        localStorage.setItem('remarkFeatureFlags', JSON.stringify(remarkFlags));
        setFeatureFlagsLoaded(true);
      }
    } else {
      // No posthog
      setFeatureFlagsLoaded(true);
    }
  }, [sessionInfo, vendor]);

  const [didInitPosthog, setDidInitPosthog] = useState(false);
  useEffect(() => {
    if (!!sessionInfo?.lead && sessionInfo.isControlGroup !== undefined && !!vendor && !didInitPosthog) {
      setDidInitPosthog(true);
      initPostHog();
    }
  }, [sessionInfo, vendor, didInitPosthog]);

  // Only show the widget if the SDK determines we should show + We are not part of the A/B test "hide" group
  const shouldShowWidget = shouldAppear && sessionInfo?.isControlGroup !== true;

  return initialized && shouldShowWidget && isWidgetVisible && featureFlagsLoaded ? (
    <AnimatePresence>
      <Activator key="activator" />
      <Chat key="chat" />
    </AnimatePresence>
  ) : (
    <></>
  );
});

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'chakra-scope': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

// Create an emotion cache tied to the given container
const memoizedCreateCacheWithContainer = weakMemoize((container: any) => {
  const newCache = createCache({ key: 'remark-iframe', container });
  return newCache;
});

type InternalWindow = Window & {
  lazySizesConfig?: { lazyClass?: string; srcAttr?: string; loadMode?: number };
};

const FrameConsumer = ({ children }: { children: React.ReactNode }) => {
  const { document, window } = useMemoizedFrame();

  useEffect(() => {
    if (!document) return;

    if (window) {
      (window as InternalWindow).lazySizesConfig = (window as InternalWindow).lazySizesConfig || {};
      (
        window as Exclude<InternalWindow, 'lazySizesConfig'> &
          Pick<Required<InternalWindow>, 'lazySizesConfig'>
      ).lazySizesConfig.lazyClass = 'remark-lazy-image';
    }

    const lazySizes: HTMLScriptElement = document.createElement('script');
    lazySizes.src = 'https://cdnjs.cloudflare.com/ajax/libs/lazysizes/5.3.2/lazysizes.min.js';
    lazySizes.integrity =
      'sha512-q583ppKrCRc7N5O0n2nzUiJ+suUv7Et1JGels4bXOaMFQcamPk9HjdUknZuuFjBNs7tsMuadge5k9RzdmO+1GQ==';
    lazySizes.crossOrigin = 'anonymous';
    document?.head.append(lazySizes);

    const lazySizesPlugin: HTMLScriptElement = document.createElement('script');
    lazySizesPlugin.src =
      'https://cdnjs.cloudflare.com/ajax/libs/lazysizes/5.3.2/plugins/parent-fit/ls.parent-fit.min.js';
    lazySizesPlugin.integrity =
      'sha512-1oXBldvRhlG5dHYmpmBFccqjN+ncdNSs6uwLtxiOufvBQy4Or63PsXibQSuokBUcY8SN7eQ3uJ4SqPM+E4xcFQ==';
    lazySizesPlugin.crossOrigin = 'anonymous';
    document?.head.append(lazySizesPlugin);

    const lazySizesPlugin2: HTMLScriptElement = document.createElement('script');
    lazySizesPlugin2.src =
      'https://cdnjs.cloudflare.com/ajax/libs/lazysizes/5.3.2/plugins/attrchange/ls.attrchange.min.js';
    lazySizesPlugin2.integrity =
      'sha512-75oykwGr4aa5tWq+sB+ylvVZ84xHqDd1n+vftcCU1WpwIIJlQi2iZNYv4aDZSO2/P0dKFNaZCnFYKDcv2yLG6g==';
    lazySizesPlugin2.crossOrigin = 'anonymous';
    document?.head.append(lazySizesPlugin);

    return () => {
      lazySizes.remove();
      lazySizesPlugin.remove();
      lazySizesPlugin2.remove();
    };
  }, [document, window]);

  return (
    // Create an emotion cache provider inside the iframe and tie it to the iframe's document
    // This stops Chakra from exploding into the main window
    <CacheProvider value={memoizedCreateCacheWithContainer(document?.head)}>{children}</CacheProvider>
  );
};

const FrameProvider = React.memo(
  ({ width, height, children }: { width: string; height: string; children: React.ReactNode }) => {
    const { vendor } = useVendor();
    return (
      <Frame
        style={{
          bottom: 0,
          right: 0,
          position: 'fixed',
          borderColor: 'none',
          borderWidth: 0,
          // Set the iframe to be the size of the widget content
          width: width,
          height: height,
          maxHeight: '100vh',
          maxWidth: '100vw',
          filter: 'drop-shadow(0 2px 24px rgba(0,0,0,0.16))',
          zIndex: vendor?.zIndex
        }}
        title="Chat with an Expert"
        initialContent='<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body><div></div></body></html>'
        ref={(node: any) => {
          if (node) {
            node.style.setProperty('bottom', '0px', 'important');
            node.style.setProperty('right', '0px', 'important');
          }
        }}
      >
        <FrameConsumer>{children}</FrameConsumer>
      </Frame>
    );
  }
);

export const App = () => {
  const config = Store.useState((s) => s.config);

  // Is the browser desktop sized?
  const [isDesktop] = useMediaQuery(DESKTOP_MEDIA_QUERY);

  // useState instead of useRef to cause a rerender when mounted
  const [divRef, setDivRef] = useState<HTMLDivElement | null>(null);
  // Keep track of the internal size of the widget
  const [size, setSize] = useState<{ height: string; width: string }>({ height: '100px', width: '100px' });

  // When the frame's content resizes, set it here.
  const resizeCallback = useCallback((size?: { height: number; width: number }) => {
    if (!size) return;
    const newSize = {
      height: `${size.height}px`,
      width: `${size.width}px`
    };
    setSize(newSize);
  }, []);

  useMeasuredSize(divRef, resizeCallback);

  // Use our hook to keep track of the browser's dimensions
  const dimensions = useWindowDimensions();

  const browserDimensions = useMemo(
    () => ({ isDesktop: isDesktop, ...dimensions }),
    [isDesktop, dimensions.width, dimensions.height]
  );

  const shopUrl = window?.Shopify?.shop ?? window?.location.host.replace(/^(www\.)/, '');
  // Remove 'www.' from the beginning of the host if it's there.
  // Leave other subdomains intact incase of something like "us." or "ca."

  if (!config.show) {
    return null;
  }

  return (
    <RemarkProvider shopUrl={shopUrl} initialPageSize={INITIAL_MESSAGE_PAGE_SIZE}>
      <BrowserDimensionsContext.Provider value={browserDimensions}>
        <FrameProvider width={size.width} height={size.height}>
          <link rel="stylesheet" href="https://unpkg.com/swiper@8/swiper-bundle.min.css" />
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
          <link
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
            rel="stylesheet"
          />

          <LabelUpdater />
          <ChakraProvider theme={theme}>
            <Flex
              // this is the content which we are tracking the size of
              ref={setDivRef}
              direction="column"
              position="fixed"
              overflow="hidden"
              bottom={0}
              right={0}
              // Using display grid with one cell to stack
              // the activator and the chat without using position: absolute
              display="grid"
              gridTemplateAreas={`"a"`}
              pointerEvents="auto"
            >
              {config && <ChatAndActivator />}
            </Flex>
          </ChakraProvider>
        </FrameProvider>
      </BrowserDimensionsContext.Provider>
    </RemarkProvider>
  );
};
